// react
import { useState } from 'react';
// libraries
import type {
  Control,
  FieldPath,
  FieldValues,
  UseControllerProps,
} from 'react-hook-form';
import { useController } from 'react-hook-form';

// shared components
import DanishAddressRegisterAutocomplete from '@/components/datafordeler/DanishAddressRegisterAutocomplete';
import type { DanishAddressRegisterSelection } from '@/components/datafordeler/DanishAddressRegisterAutocomplete';
import useDarReverseGeocode from '@/components/datafordeler/useDarReverseGeocode';

// relative
import {
  buildDanishAddressFieldValue,
  EMPTY_ADDRESS_VALUE,
} from './danishAddressFieldValue';
import type { DanishAddressFieldValue } from './danishAddressFieldValue';

export type RHFAddressAutocompleteProps<TFieldValues extends FieldValues> = {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  label?: string;
  placeholder?: string;
  helperText?: string;
  minSearchChars?: number;
  maxResults?: number;
  rules?: UseControllerProps<TFieldValues>['rules'];
  'data-testid'?: string;
};

export default function RHFAddressAutocomplete<
  TFieldValues extends FieldValues,
>({
  name,
  control,
  rules,
  required,
  disabled,
  fullWidth,
  size,
  label,
  placeholder,
  helperText,
  minSearchChars,
  maxResults,
  'data-testid': testId,
}: RHFAddressAutocompleteProps<TFieldValues>) {
  const { field, fieldState } = useController({
    name,
    control,
    rules,
  });

  const [isRefiningAddress, setIsRefiningAddress] = useState(false);

  const value =
    (field.value as DanishAddressFieldValue | null | undefined) ??
    EMPTY_ADDRESS_VALUE;

  // Reverse geocode initial coords (e.g. an existing gateway/device location)
  // so the address input has a label even before the user searches.
  const reverseGeocodedLabel = useDarReverseGeocode(
    value.displayName === '' ? value.latitude : null,
    value.displayName === '' ? value.longitude : null,
  );

  const displayLabel = value.displayName || reverseGeocodedLabel || '';

  const handleAddressSelect = (
    selection: DanishAddressRegisterSelection | null,
  ) => {
    setIsRefiningAddress(false);
    field.onChange(buildDanishAddressFieldValue(selection));
  };

  const handleRefinementChange = (isRefining: boolean) => {
    setIsRefiningAddress(isRefining);
  };

  const errorMessage = isRefiningAddress
    ? 'Select a complete address'
    : fieldState.error?.message;

  return (
    <DanishAddressRegisterAutocomplete
      value={displayLabel}
      onAddressSelect={handleAddressSelect}
      onRefinementChange={handleRefinementChange}
      minSearchChars={minSearchChars}
      maxResults={maxResults}
      autocompleteProps={{
        disabled,
        fullWidth,
        size,
        onBlur: field.onBlur,
      }}
      textFieldProps={{
        required,
        label,
        placeholder,
        error: isRefiningAddress || Boolean(fieldState.error),
        helperText: errorMessage ?? helperText,
        'data-testid': testId,
      }}
    />
  );
}
